import { css } from "@emotion/react"
import { Capacitor } from "@capacitor/core"
import CloseIcon from "@mui/icons-material/Close"
import { Button, Typography } from "matsuri-ui"
import { useMemo, useState } from "react"
import { useLocation } from "react-router-dom"
import { theme } from "@/theme"
import { useTranslation } from "@/hooks/useTranslation"
import { ANDROID_PLAY_STORE_URL, IOS_APP_STORE_URL } from "@/constants/appStore"

const APP_DOWNLOAD_BANNER_DISMISSED_KEY = "app_download_banner_dismissed"

export const AppDownloadBanner = () => {
    const location = useLocation()

    // セッション状態管理
    const [isVisible, setIsVisible] = useState(() => {
        try {
            return (
                sessionStorage.getItem(APP_DOWNLOAD_BANNER_DISMISSED_KEY) !==
                "true"
            )
        } catch {
            // sessionStorageが利用できない環境では、バナーを表示しない
            return false
        }
    })

    // OS判定（モバイルのみ対応、デスクトップは非表示）
    const appStoreUrl = useMemo(() => {
        const ua = window.navigator.userAgent
        const isIOS = ua.includes("iPhone") || ua.includes("iPad")
        const isAndroid = ua.includes("Android")

        if (isIOS) {
            return IOS_APP_STORE_URL
        }
        if (isAndroid) {
            return ANDROID_PLAY_STORE_URL
        }
        // デスクトップ（Windows、macOS、Linux等）は非表示
        return null
    }, [])

    const { t } = useTranslation()

    // バナー表示判定（早期リターン）
    if (
        Capacitor.isNativePlatform() ||
        !isVisible ||
        !appStoreUrl ||
        location.pathname.startsWith("/reports") || // /reports 配下のページでは非表示
        location.pathname.startsWith("/tour") // /tour 配下のページでは非表示
    ) {
        return null
    }

    // 閉じる処理
    const handleDismiss = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        try {
            sessionStorage.setItem(APP_DOWNLOAD_BANNER_DISMISSED_KEY, "true")
        } catch (error) {
            console.error("sessionStorage is not available", error)
        }
        setIsVisible(false)
    }

    return (
        <div
            css={css`
                position: relative;
                z-index: ${theme.zIndex.appBar};
                width: 100%;
                max-width: ${theme.device.maxWidth}px;
                margin: 0 auto;
                background-color: #ffffff;
                border-bottom: 1px solid #e0e0e0;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            `}
            role="region"
            aria-label={t("アプリダウンロード案内")}
            aria-live="polite"
        >
            <div
                css={css`
                    display: flex;
                    gap: 4px;
                    align-items: center;
                    padding: 12px 16px;
                `}
            >
                <a
                    href={appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    css={css`
                        display: flex;
                        flex: 1;
                        gap: 12px;
                        align-items: center;
                        color: inherit;
                        text-decoration: none;
                    `}
                >
                    <img
                        src="/assets/app-download-banner-icon.png"
                        alt={t("M2M Cleaningアプリアイコン")}
                        css={css`
                            flex-shrink: 0;
                            width: 48px;
                            height: 48px;
                        `}
                    />
                    <div
                        css={css`
                            display: flex;
                            flex: 1;
                            flex-direction: column;
                            gap: 2px;
                        `}
                    >
                        <Typography
                            variant="body"
                            css={css`
                                font-size: 14px;
                                font-weight: 600;
                                color: ${theme.palette.intentions.primary.main};
                                letter-spacing: 0.8px;
                            `}
                        >
                            {t("アプリでもっと快適に")}
                        </Typography>
                        <Typography
                            variant="caption"
                            css={css`
                                font-size: 12px;
                                color: #666;
                                letter-spacing: 0.8px;
                            `}
                        >
                            {t("鍵のスマホ解錠に対応")}
                        </Typography>
                    </div>
                </a>
                <Button
                    as="a"
                    href={appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="filled"
                    size="small"
                    css={css`
                        flex-shrink: 0;
                        font-size: 14px;
                        color: ${theme.palette.intentions.primary.contrastText};
                        text-decoration: none;
                        white-space: nowrap;
                        background-color: ${
                            theme.palette.intentions.primary.main
                        };
                        border: none;
                        box-shadow: none;
                    `}
                >
                    {t("インストール")}
                </Button>
                <button
                    onClick={handleDismiss}
                    aria-label={t("バナーを閉じる")}
                    css={css`
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        min-width: 32px;
                        min-height: 32px;
                        padding: 4px;
                        color: #333;
                        cursor: pointer;
                        background-color: transparent;
                        border: none;
                        transition: color 0.2s;
                    `}
                >
                    <CloseIcon fontSize="small" />
                </button>
            </div>
        </div>
    )
}
